export const PARTY_PROTOCOL_VERSION = 1;
export const PARTY_SOCKET_PATH = '/ws/party';
export const PARTY_ROOM_CODE_LENGTH = 6;
export const PARTY_ROOM_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{6}$/;
export const PARTY_NICKNAME_MAX_LENGTH = 10;
export const PARTY_ACTION_SEQUENCE_MAX = Number.MAX_SAFE_INTEGER - 1;
export const PARTY_PUBLIC_AUTO_START_DELAY_MILLISECONDS = 6_000;

export const PARTY_GAME_IDS = [
  'drawing-relay',
  'tail-chase',
  'kong-fortress',
] as const;

export const PARTY_PUBLIC_GAME_IDS = [
  'tail-chase',
  'kong-fortress',
] as const;

export type PartyGameId = (typeof PARTY_GAME_IDS)[number];
export type PartyPublicGameId = (typeof PARTY_PUBLIC_GAME_IDS)[number];
export type PartyRoomPhase = 'lobby' | 'playing' | 'results';
export type PartyRoomVisibility = 'private' | 'public';

export const PARTY_GAME_LIMITS: Readonly<Record<PartyGameId, {
  readonly minPlayers: number;
  readonly maxPlayers: number;
}>> = {
  'drawing-relay': { minPlayers: 3, maxPlayers: 8 },
  'tail-chase': { minPlayers: 2, maxPlayers: 4 },
  'kong-fortress': { minPlayers: 2, maxPlayers: 4 },
};

export interface PartyPlayerView {
  readonly id: string;
  readonly nickname: string;
  readonly colorIndex: number;
  readonly ready: boolean;
  readonly connected: boolean;
  readonly isHost: boolean;
}

export interface PartyRoomView {
  readonly roomCode: string;
  readonly gameId: PartyGameId;
  readonly visibility: PartyRoomVisibility;
  readonly phase: PartyRoomPhase;
  readonly minPlayers: number;
  readonly maxPlayers: number;
  readonly hostId: string;
  readonly players: readonly PartyPlayerView[];
  readonly rematchReadyPlayerIds: readonly string[];
  readonly autoStartAt: number | null;
  readonly game: unknown;
  readonly serverTime: number;
}

export interface PartyPublicRoomSummary {
  readonly roomCode: string;
  readonly gameId: PartyPublicGameId;
  readonly hostNickname: string;
  readonly playerCount: number;
  readonly maxPlayers: number;
  readonly createdAt: number;
  readonly autoStartAt: number | null;
}

export type PartyClientMessage =
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'create';
    readonly gameId: PartyGameId;
    readonly nickname: string;
    readonly visibility?: PartyRoomVisibility;
  }
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'join';
    readonly gameId: PartyGameId;
    readonly roomCode: string;
    readonly nickname: string;
    readonly token?: string;
  }
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'list-public';
    readonly gameId: PartyPublicGameId;
  }
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'quick-join';
    readonly gameId: PartyPublicGameId;
    readonly nickname: string;
  }
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'ready';
    readonly ready: boolean;
  }
  | { readonly v: typeof PARTY_PROTOCOL_VERSION; readonly type: 'start' }
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'action';
    readonly seq: number;
    readonly action: Readonly<Record<string, unknown>>;
  }
  | { readonly v: typeof PARTY_PROTOCOL_VERSION; readonly type: 'rematch' }
  | { readonly v: typeof PARTY_PROTOCOL_VERSION; readonly type: 'leave' }
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'ping';
    readonly clientTime: number;
  };

export type PartyServerErrorCode =
  | 'INVALID_MESSAGE'
  | 'VERSION_MISMATCH'
  | 'ROOM_NOT_FOUND'
  | 'ROOM_FULL'
  | 'ROOM_STARTED'
  | 'GAME_MISMATCH'
  | 'GAME_UNAVAILABLE'
  | 'PUBLIC_GAME_UNAVAILABLE'
  | 'PUBLIC_NICKNAME_REJECTED'
  | 'NOT_HOST'
  | 'NOT_READY'
  | 'INVALID_ACTION'
  | 'RATE_LIMITED'
  | 'SERVER_FULL';

export type PartyRoomClosedReason =
  | 'left'
  | 'room-expired'
  | 'server-shutdown';

export type PartyServerMessage =
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'public-rooms';
    readonly gameId: PartyPublicGameId;
    readonly rooms: readonly PartyPublicRoomSummary[];
    readonly serverTime: number;
  }
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'room';
    readonly token: string;
    readonly playerId: string;
    readonly nextActionSeq: number;
    readonly reconnected: boolean;
    readonly room: PartyRoomView;
  }
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'state';
    readonly room: PartyRoomView;
  }
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'event';
    readonly name: string;
    readonly payload: unknown;
    readonly serverTime: number;
  }
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'closed';
    readonly reason: PartyRoomClosedReason;
  }
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'error';
    readonly code: PartyServerErrorCode;
    readonly message: string;
    readonly retryable: boolean;
  }
  | {
    readonly v: typeof PARTY_PROTOCOL_VERSION;
    readonly type: 'pong';
    readonly clientTime: number;
    readonly serverTime: number;
  };

export function isPartyGameId(value: unknown): value is PartyGameId {
  return typeof value === 'string' && PARTY_GAME_IDS.some((gameId) => gameId === value);
}

export function isPartyPublicGameId(value: unknown): value is PartyPublicGameId {
  return typeof value === 'string'
    && PARTY_PUBLIC_GAME_IDS.some((gameId) => gameId === value);
}

export function normalizePartyRoomCode(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, PARTY_ROOM_CODE_LENGTH);
}

export function isPartyRoomCode(value: unknown): value is string {
  return typeof value === 'string' && PARTY_ROOM_CODE_PATTERN.test(value);
}

export function normalizePartyNickname(value: string): string {
  return Array.from(value.trim().replace(/\s+/g, ' ')).slice(0, PARTY_NICKNAME_MAX_LENGTH).join('');
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isProtocolVersion(value: unknown): value is typeof PARTY_PROTOCOL_VERSION {
  return value === PARTY_PROTOCOL_VERSION;
}

export function parsePartyClientMessage(value: string): PartyClientMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (!isPlainRecord(parsed) || !isProtocolVersion(parsed.v) || typeof parsed.type !== 'string') {
    return null;
  }

  if (parsed.type === 'create'
    && isPartyGameId(parsed.gameId)
    && typeof parsed.nickname === 'string'
    && (parsed.visibility === undefined
      || parsed.visibility === 'private'
      || parsed.visibility === 'public')) {
    const nickname = normalizePartyNickname(parsed.nickname);
    return nickname
      ? {
        v: PARTY_PROTOCOL_VERSION,
        type: 'create',
        gameId: parsed.gameId,
        nickname,
        ...(parsed.visibility ? { visibility: parsed.visibility } : {}),
      }
      : null;
  }

  if (parsed.type === 'join'
    && isPartyGameId(parsed.gameId)
    && isPartyRoomCode(parsed.roomCode)
    && typeof parsed.nickname === 'string'
    && (parsed.token === undefined || typeof parsed.token === 'string')) {
    const nickname = normalizePartyNickname(parsed.nickname);
    if (!nickname) return null;
    return {
      v: PARTY_PROTOCOL_VERSION,
      type: 'join',
      gameId: parsed.gameId,
      roomCode: parsed.roomCode,
      nickname,
      ...(parsed.token ? { token: parsed.token.slice(0, 128) } : {}),
    };
  }

  if (parsed.type === 'list-public' && isPartyPublicGameId(parsed.gameId)) {
    return { v: PARTY_PROTOCOL_VERSION, type: 'list-public', gameId: parsed.gameId };
  }

  if (parsed.type === 'quick-join'
    && isPartyPublicGameId(parsed.gameId)
    && typeof parsed.nickname === 'string') {
    const nickname = normalizePartyNickname(parsed.nickname);
    return nickname
      ? { v: PARTY_PROTOCOL_VERSION, type: 'quick-join', gameId: parsed.gameId, nickname }
      : null;
  }

  if (parsed.type === 'ready' && typeof parsed.ready === 'boolean') {
    return { v: PARTY_PROTOCOL_VERSION, type: 'ready', ready: parsed.ready };
  }

  if (parsed.type === 'start' || parsed.type === 'rematch' || parsed.type === 'leave') {
    return { v: PARTY_PROTOCOL_VERSION, type: parsed.type };
  }

  if (parsed.type === 'action'
    && Number.isSafeInteger(parsed.seq)
    && Number(parsed.seq) >= 0
    && Number(parsed.seq) <= PARTY_ACTION_SEQUENCE_MAX
    && isPlainRecord(parsed.action)
    && typeof parsed.action.type === 'string'
    && parsed.action.type.length > 0
    && parsed.action.type.length <= 40) {
    return {
      v: PARTY_PROTOCOL_VERSION,
      type: 'action',
      seq: Number(parsed.seq),
      action: parsed.action,
    };
  }

  if (parsed.type === 'ping' && typeof parsed.clientTime === 'number' && Number.isFinite(parsed.clientTime)) {
    return { v: PARTY_PROTOCOL_VERSION, type: 'ping', clientTime: parsed.clientTime };
  }

  return null;
}

function isPartyPlayerView(value: unknown): value is PartyPlayerView {
  if (!isPlainRecord(value)) return false;
  return typeof value.id === 'string'
    && typeof value.nickname === 'string'
    && Number.isSafeInteger(value.colorIndex)
    && typeof value.ready === 'boolean'
    && typeof value.connected === 'boolean'
    && typeof value.isHost === 'boolean';
}

function isPartyRoomView(value: unknown): value is PartyRoomView {
  if (!isPlainRecord(value)) return false;
  return isPartyRoomCode(value.roomCode)
    && isPartyGameId(value.gameId)
    && (value.visibility === 'private' || value.visibility === 'public')
    && (value.phase === 'lobby' || value.phase === 'playing' || value.phase === 'results')
    && Number.isSafeInteger(value.minPlayers)
    && Number.isSafeInteger(value.maxPlayers)
    && typeof value.hostId === 'string'
    && Array.isArray(value.players)
    && value.players.every(isPartyPlayerView)
    && Array.isArray(value.rematchReadyPlayerIds)
    && value.rematchReadyPlayerIds.every((playerId) => typeof playerId === 'string')
    && (value.autoStartAt === null || typeof value.autoStartAt === 'number')
    && typeof value.serverTime === 'number';
}

function isPartyPublicRoomSummary(value: unknown): value is PartyPublicRoomSummary {
  if (!isPlainRecord(value)) return false;
  return isPartyRoomCode(value.roomCode)
    && isPartyPublicGameId(value.gameId)
    && typeof value.hostNickname === 'string'
    && Number.isSafeInteger(value.playerCount)
    && Number(value.playerCount) >= 1
    && Number.isSafeInteger(value.maxPlayers)
    && Number(value.maxPlayers) >= Number(value.playerCount)
    && typeof value.createdAt === 'number'
    && (value.autoStartAt === null || typeof value.autoStartAt === 'number');
}

export function parsePartyServerMessage(value: string): PartyServerMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (!isPlainRecord(parsed) || !isProtocolVersion(parsed.v) || typeof parsed.type !== 'string') {
    return null;
  }
  if (parsed.type === 'room'
    && typeof parsed.token === 'string'
    && typeof parsed.playerId === 'string'
    && Number.isSafeInteger(parsed.nextActionSeq)
    && Number(parsed.nextActionSeq) >= 0
    && typeof parsed.reconnected === 'boolean'
    && isPartyRoomView(parsed.room)) {
    return parsed as PartyServerMessage;
  }
  if (parsed.type === 'state' && isPartyRoomView(parsed.room)) return parsed as PartyServerMessage;
  if (parsed.type === 'public-rooms'
    && isPartyPublicGameId(parsed.gameId)
    && Array.isArray(parsed.rooms)
    && parsed.rooms.every(isPartyPublicRoomSummary)
    && typeof parsed.serverTime === 'number') {
    return parsed as PartyServerMessage;
  }
  if (parsed.type === 'event'
    && typeof parsed.name === 'string'
    && typeof parsed.serverTime === 'number') {
    return parsed as PartyServerMessage;
  }
  if (parsed.type === 'closed'
    && (parsed.reason === 'left' || parsed.reason === 'room-expired' || parsed.reason === 'server-shutdown')) {
    return parsed as PartyServerMessage;
  }
  if (parsed.type === 'error'
    && typeof parsed.code === 'string'
    && typeof parsed.message === 'string'
    && typeof parsed.retryable === 'boolean') {
    return parsed as PartyServerMessage;
  }
  if (parsed.type === 'pong'
    && typeof parsed.clientTime === 'number'
    && typeof parsed.serverTime === 'number') {
    return parsed as PartyServerMessage;
  }
  return null;
}
